import {observer} from 'mobx-react-lite'
import React, {useMemo} from 'react'
import tw from 'tailwind-styled-components'
import {Badge} from '@components/common/base/badge'
import {LimitedHighlight} from '@components/common/highlight/highlight'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {cn} from '@containers/html-utils'
import {useMyStickersStore} from '../../../my-stickers-store'
import {StickerLibrary, Tree} from '../../../my-stickers-utils'

const TwRow = tw.div<{$highlight: boolean}>`
  self-start
  flex-1
  min-w-0
  truncate
  text-xs-regular
  text-secondary
  ${p => cn(!p.$highlight && 'flex items-center')}
`

const TwTree = tw.div`
  max-w-240
  flex
  flex-col
  gap-4
  text-invert
  text-xs-regular
`

const TreeContent = ({tree}: {tree: Tree[]}): React.ReactElement => (
  <TwTree>
    {tree.map(tree => (
      <span key={tree.project.id}>
        {`${tree.project.title} (${tree.boards
          .map(board => board.title)
          .join(', ')})`}
      </span>
    ))}
  </TwTree>
)

const getTreeText = (tree: Tree[]): string => {
  return tree
    .map(
      tree =>
        `${tree.project.title} (${tree.boards
          .map(board => board.title)
          .join(', ')})`
    )
    .join(', ')
}

export const ListItemProjectsAndBoards = observer(
  ({
    stickerLibrary,
    highlight = false
  }: {
    stickerLibrary: StickerLibrary
    highlight?: boolean
  }) => {
    const [state] = useMyStickersStore()
    const text = useMemo(
      () => getTreeText(stickerLibrary.tree),
      [stickerLibrary.tree]
    )

    return (
      <Tooltip
        content={<TreeContent tree={stickerLibrary.tree} />}
        placement="bottom"
        offset={[0, 4]}
      >
        <TwRow $highlight={highlight}>
          {highlight ? (
            <LimitedHighlight highlight={state.searchQuery} text={text} />
          ) : (
            <>
              <span className="mr-4 truncate">
                {stickerLibrary.tree[0] && (
                  <>
                    <span className="mr-4">
                      {stickerLibrary.tree[0].project.title}
                    </span>
                    <span className="text-secondary">
                      {stickerLibrary.tree[0].boards
                        .map(board => board.title)
                        .join(', ')
                        .slice(0, 20)}
                    </span>
                  </>
                )}
              </span>
              {stickerLibrary.tree.length > 1 && (
                <Badge type="rounded">+{stickerLibrary.tree.length - 1}</Badge>
              )}
            </>
          )}
        </TwRow>
      </Tooltip>
    )
  }
)
